import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Clock, Printer, Calendar } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-texture">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-sm border-b border-[#E5E5E5]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <h1 
            className="text-2xl text-[#1A1A1A]"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="logo"
          >
            My Weekly Journey
          </h1>
          <Button 
            onClick={() => navigate('/create')}
            className="rounded-full px-6 py-2 bg-[#1A1A1A] text-[#FDFBF7] hover:bg-[#333333] transition-all duration-300"
            data-testid="nav-create-btn"
          >
            Create Card
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pb-32 px-6 md:px-12 lg:px-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Left - Text */}
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#737373] mb-6">
                The most meaningful birthday card
              </p>
              <h1 
                className="text-5xl sm:text-6xl lg:text-7xl text-[#1A1A1A] leading-[0.9] tracking-tight mb-8"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                4,680<br />
                <span className="text-[#9A3B3B]">Weeks</span>
              </h1>
              <p className="text-lg md:text-xl text-[#1A1A1A] font-light leading-relaxed mb-10 max-w-md">
                A life is roughly 4,680 weeks. Gift someone the perspective 
                to treasure each one with a personalised "Life in Weeks" birthday card.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/create')}
                  className="rounded-full px-8 py-6 text-lg font-medium bg-[#1A1A1A] text-[#FDFBF7] hover:bg-[#333333] transition-all duration-300"
                  data-testid="hero-create-btn"
                >
                  Create Your Card
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Right - Visual */}
            <div>
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                  {/* Mini grid preview - 20 cols x 26 rows = 520 cells */}
                  <div 
                    className="grid gap-[2px] mb-6"
                    style={{ gridTemplateColumns: 'repeat(20, 1fr)' }}
                  >
                    {[...Array(520)].map((_, i) => (
                      <div 
                        key={i}
                        className="w-2 h-2 rounded-[1px]"
                        style={{
                          backgroundColor: i < 200 ? '#1A1A1A' : 'transparent',
                          border: i >= 200 ? '1px solid #E5E5E5' : 'none'
                        }}
                      />
                    ))}
                  </div>
                  <p 
                    className="text-center text-lg text-[#1A1A1A] italic"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    "Make every week count."
                  </p>
                </div>
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#9A3B3B]/10 rounded-full blur-3xl" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#1A1A1A]/5 rounded-full blur-3xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white" data-testid="value-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#737373] mb-6">
            A different kind of card
          </p>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            The most meaningful card<br />
            you will ever give
          </h2>
          <p className="text-lg text-[#737373] font-light leading-relaxed max-w-2xl mx-auto">
            In a world of forgettable birthday cards, give something that sparks 
            reflection. A visual reminder that life is finite, precious, and best 
            lived fully in the present moment.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#737373] mb-4">
              Simple process
            </p>
            <h2 
              className="text-3xl md:text-4xl text-[#1A1A1A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              How it works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {/* Step 1 */}
            <div className="text-center" data-testid="step-1">
              <div className="w-16 h-16 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-6">
                <Calendar className="h-7 w-7 text-[#FDFBF7]" strokeWidth={1.5} />
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#9A3B3B] mb-2">Step 1</p>
              <h3 
                className="text-xl text-[#1A1A1A] mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Enter birthdate
              </h3>
              <p className="text-[#737373] font-light">
                Select the recipient's date of birth to calculate their life in weeks.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center" data-testid="step-2">
              <div className="w-16 h-16 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-6">
                <Clock className="h-7 w-7 text-[#FDFBF7]" strokeWidth={1.5} />
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#9A3B3B] mb-2">Step 2</p>
              <h3 
                className="text-xl text-[#1A1A1A] mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Preview your card
              </h3>
              <p className="text-[#737373] font-light">
                Watch the "life in weeks" grid come alive with their lived experience.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center" data-testid="step-3">
              <div className="w-16 h-16 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-6">
                <Printer className="h-7 w-7 text-[#FDFBF7]" strokeWidth={1.5} />
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#9A3B3B] mb-2">Step 3</p>
              <h3 
                className="text-xl text-[#1A1A1A] mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Print & fold
              </h3>
              <p className="text-[#737373] font-light">
                Download as PDF, print double-sided on A4, and fold into a beautiful card.
              </p>
            </div>
          </div>

          <div className="text-center mt-16">
            <Button 
              onClick={() => navigate('/create')}
              className="rounded-full px-8 py-6 text-lg font-medium bg-[#1A1A1A] text-[#FDFBF7] hover:bg-[#333333] transition-all duration-300"
              data-testid="how-it-works-create-btn"
            >
              Create Your Card Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#1A1A1A]" data-testid="philosophy-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#9A3B3B] mb-8">
            The philosophy
          </p>
          <blockquote 
            className="text-2xl md:text-3xl lg:text-4xl text-[#FDFBF7] leading-relaxed mb-8 italic"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            "Life is not measured by the number of breaths we take, 
            but by the moments that take our breath away."
          </blockquote>
          <p className="text-[#737373]">— Maya Angelou</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-[#E5E5E5]" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p 
            className="text-xl text-[#1A1A1A]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            My Weekly Journey
          </p>
          <p className="text-sm text-[#737373]">
            A gift of perspective. Make every week count.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
